import Fade from '@mui/material/Fade'
import { MainApi } from 'api/MainApi'
import { StyledDropdown } from 'components/Dropdown/StyledDropdown'
import Loader from 'components/Loader/Loader'
import { ReactComponent as DropdownIcon } from 'images/icons/arrow_select.svg'
import { ReactComponent as LocationSvg } from 'images/icons/location.svg'
import { ReactComponent as MappingSvg } from 'images/icons/mapping.svg'
import { ReactComponent as SatelliteSvg } from 'images/icons/satellite.svg'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES } from 'utils/constants'

import style from './RecordedRouteSelect.module.css'

// ... imports are fine ...

interface RecordedRouteSelectProps {
  onSelect: (name: string, id: number) => void
  placeholder?: string
  value?: string
  description?: string
  isSlamMode?: boolean
}

const renderLocalizationMode = (mode: number | undefined) => {
  switch (mode) {
    case 1:
      return <MappingSvg width={18} height={18} className={style.themedIcon} title="SLAM" />
    case 2:
      return <SatelliteSvg width={18} height={18} className={style.themedIcon} title="GNSS" />
    case 3:
      return (
        <>
          <SatelliteSvg width={18} height={18} className={style.themedIcon} title="GNSS" />
          <span>/</span>
          <MappingSvg width={18} height={18} className={style.themedIcon} title="SLAM" />
        </>
      )
    default:
      return '—'
  }
}

const FileItem = ({ file, facilities = [], onClick }: { file: any; facilities?: any[]; onClick: () => void }) => {
  const displayName = file.name ? file.name.replace(/\.[^/.]+$/, '') : 'Unnamed file'
  const isSlamBased = file.localizationMode === 1 || file.localizationMode === 3
  const mapNameText = isSlamBased ? file.lidarMapName || '—' : '—'
  const facilityName = file.facilityId
    ? facilities.find((f: any) => f.id === file.facilityId)?.name || `ID: ${file.facilityId}`
    : '—'

  return (
    <div className={style.dropdownItem} onClick={onClick}>
      <span
        style={{
          flex: '1 1 0%',
          minWidth: 0,
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          hyphens: 'auto',
          WebkitHyphens: 'auto',
          msHyphens: 'auto',
        }}
      >
        {displayName}
      </span>
      <span
        style={{
          flex: '0 0 120px',
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {facilityName}
      </span>
      <span className={style.localizationCol} style={{ flex: '0 0 80px' }}>
        {renderLocalizationMode(file.localizationMode)}
      </span>
      <span
        style={{
          flex: '0 0 130px',
          textAlign: 'center',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          hyphens: 'auto',
          WebkitHyphens: 'auto',
          msHyphens: 'auto',
        }}
        title={mapNameText}
      >
        {mapNameText}
      </span>
    </div>
  )
}

const PAGE_SIZE = 20

const RecordedRouteSelect = ({ onSelect, placeholder, value, description, isSlamMode }: RecordedRouteSelectProps) => {
  const { t } = useTranslation()
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [files, setFiles] = useState<any[]>([])
  const [facilities, setFacilities] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        const response = await MainApi.fetchFacilities(1, 100)
        setFacilities(response.results || [])
      } catch (err) {
        console.error('Error fetching facilities:', err)
      }
    }
    loadFacilities()
  }, [])

  const currentFetchIdRef = useRef(0)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const id = useId()

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchData = async (pageNum: number, search: string, isAppend = false) => {
    currentFetchIdRef.current += 1
    const fetchId = currentFetchIdRef.current

    try {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.WORK)
      } else {
        setIsFetchingMore(true)
      }
      const response = await MainApi.fetchRecordedRoutes(pageNum, PAGE_SIZE, search || undefined)

      if (fetchId !== currentFetchIdRef.current) return

      let results = response.results
      if (isSlamMode !== undefined) {
        results = results.filter(route => {
          const routeLocMode = route.properties.localizationMode
          if (isSlamMode) {
            return (
              routeLocMode === 1 ||
              routeLocMode === 3 ||
              routeLocMode === 0 ||
              routeLocMode === undefined ||
              routeLocMode === null
            )
          } else {
            return (
              routeLocMode === 2 ||
              routeLocMode === 3 ||
              routeLocMode === 0 ||
              routeLocMode === undefined ||
              routeLocMode === null
            )
          }
        })
      }

      const mappedFiles = results.map(route => ({
        name: route.properties.name || route.properties.filename || '',
        id: route.id,
        size: 0,
        modified: route.properties.recordedAt ? new Date(route.properties.recordedAt).getTime() / 1000 : 0,
        localizationMode: route.properties.localizationMode,
        lidarMapName: route.properties.lidarMapName,
        facilityId: route.properties.facilityId,
      }))

      if (isAppend) {
        setFiles(prev => [...prev, ...mappedFiles])
      } else {
        setFiles(mappedFiles)
      }

      setHasMore(pageNum * PAGE_SIZE < response.count)
      setPage(pageNum)

      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      }
    } catch (error) {
      if (fetchId !== currentFetchIdRef.current) return
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.ERROR)
      }
      console.error('Error fetching files:', error)
    } finally {
      if (fetchId === currentFetchIdRef.current) {
        setIsFetchingMore(false)
      }
    }
  }

  useEffect(() => {
    if (open) {
      if (files.length === 0 || debouncedSearchQuery !== '') {
        fetchData(1, debouncedSearchQuery, false)
      }
      // Focus input when opened
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedSearchQuery])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!open) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (loadingProgress === PROGRESS_TYPES.SUCCESS && hasMore && !isFetchingMore) {
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        fetchData(page + 1, debouncedSearchQuery, true)
      }
    }
  }

  const handleFileSelect = (file: any) => {
    onSelect(file.name, file.id)
    handleClose()
  }

  const handleClickTrigger = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setTimeout(() => {
      setPage(1)
      setHasMore(true)
      setIsFetchingMore(false)
      setSearchQuery('')
      setDebouncedSearchQuery('')
      setFiles([])
    }, 300)
  }

  const dropdownWidth = anchorEl?.clientWidth

  return (
    <div className={style.container}>
      {description && <div className={style.subtitle}>{description}</div>}
      <div className={style.trigger} onClick={handleClickTrigger}>
        <div className={`${style.triggerText} ${!value ? style.placeholderColor : ''}`}>
          {value || placeholder || t('zone_details.select_recorded_route')}
        </div>
        <div className={`${style.triggerIcon} ${open ? style.triggerIconOpen : ''}`}>
          <DropdownIcon />
        </div>
      </div>

      {/* ... rest of the component ... */}

      <StyledDropdown
        MenuListProps={{ 'aria-labelledby': id }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        container={typeof document !== 'undefined' ? () => document.body : undefined}
        disablePortal={false}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        PaperProps={{
          style: dropdownWidth ? { width: dropdownWidth, maxHeight: 400 } : undefined,
        }}
      >
        <div className={style.searchContainer} onClick={e => e.stopPropagation()}>
          <input
            ref={inputRef}
            className={style.searchInput}
            type="text"
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
          />
        </div>

        <div className={style.listContainer} onScroll={handleScroll}>
          {loadingProgress === PROGRESS_TYPES.WORK && (
            <div className={style.loading}>
              <Loader />
            </div>
          )}

          <div className={style.tableHeader}>
            <span style={{ flex: '1 1 0%', minWidth: 0 }}>Name</span>
            <span style={{ flex: '0 0 120px', textAlign: 'center' }}>Facility</span>
            <span className={style.localizationCol} style={{ flex: '0 0 80px' }}>
              <LocationSvg width={18} height={18} className={style.themedIcon} title="Localization" />
            </span>
            <span style={{ flex: '0 0 130px', textAlign: 'center' }}>Map name</span>
          </div>

          {files.map((file, index) => (
            <FileItem
              key={`${file.id || file.name}-${index}`}
              file={file}
              facilities={facilities}
              onClick={() => handleFileSelect(file)}
            />
          ))}

          {loadingProgress === PROGRESS_TYPES.SUCCESS && files.length === 0 && (
            <div className={style.loading}>No items found</div>
          )}

          {loadingProgress === PROGRESS_TYPES.ERROR && <div className={style.loading}>Error loading routes</div>}

          {isFetchingMore && (
            <div className={style.loading}>
              <Loader />
            </div>
          )}
        </div>
      </StyledDropdown>
    </div>
  )
}

export default RecordedRouteSelect
